/**
 * RepBuddy - Memory Service
 * What RepBuddy remembers about the rep (usual titles, states, cap, avoid
 * list, notes), read and written through the authenticated backend API.
 *
 * Reads never throw: no session or no backend means "no memory yet", and the
 * app behaves exactly as it did before memory existed. Writes are best-effort
 * for the same reason; a lost event costs one tally, never a search.
 */

import { supabase } from '../lib/supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export const MEMORY_STORAGE_KEY = 'repbuddy_memory';

async function authedHeaders() {
  const base = { 'Content-Type': 'application/json' };
  if (!supabase) return base;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return base;
    return { ...base, Authorization: `Bearer ${session.access_token}` };
  } catch {
    return base;
  }
}

/** The last memory this browser saw, for a synchronous first render. */
export function loadMemoryCached() {
  try { return JSON.parse(localStorage.getItem(MEMORY_STORAGE_KEY) || 'null'); }
  catch { return null; }
}

export function saveMemoryCached(memory) {
  try {
    if (memory) localStorage.setItem(MEMORY_STORAGE_KEY, JSON.stringify(memory));
    else localStorage.removeItem(MEMORY_STORAGE_KEY);
  } catch {}
}

/** GET /api/memory. Resolves the memory object, or null when unavailable. */
export async function fetchMemory() {
  try {
    const headers = await authedHeaders();
    if (!headers.Authorization) return null;
    const r = await fetch(`${BACKEND_URL}/api/memory`, { headers });
    if (!r.ok) return null;
    const { memory } = await r.json();
    return memory || null;
  } catch {
    return null;
  }
}

/**
 * POST /api/memory/events. The server folds the events into the stored blob
 * and returns the result, so two tabs cannot overwrite each other with stale
 * copies. Resolves the updated memory, or null when the write did not happen.
 */
export async function sendMemoryEvents(events) {
  if (!Array.isArray(events) || events.length === 0) return null;
  try {
    const headers = await authedHeaders();
    if (!headers.Authorization) return null;
    const r = await fetch(`${BACKEND_URL}/api/memory/events`, {
      method: 'POST', headers, body: JSON.stringify({ events }),
    });
    if (!r.ok) return null;
    const { memory } = await r.json();
    return memory || null;
  } catch {
    return null;
  }
}

/**
 * PUT /api/memory. For the Account page: the user edited the blob directly.
 * `allowEmpty` is required to store a blank memory ("Forget everything"), so a
 * bug can never wipe a rep's memory by accident. Throws on failure: this is a
 * button the user pressed, and silence would read as success.
 */
export async function saveMemory(memory, { allowEmpty = false } = {}) {
  const headers = await authedHeaders();
  if (!headers.Authorization) throw new Error('Please sign in to change what RepBuddy remembers.');
  const r = await fetch(`${BACKEND_URL}/api/memory`, {
    method: 'PUT', headers, body: JSON.stringify({ memory, allowEmpty }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `Could not save (${r.status})`);
  return data.memory || memory;
}
